import { select } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { createFeatureSelector } from 'features/create-feature-selector';
import { getPathname } from 'common/selectors';
import { PriceCategoryGaNames } from 'features/ga-events/messages/types';
import trimEnd from 'lodash.trimend';
import { EffectR } from 'types/utils';
import type { Price, ProductMainInfoProps, SecondPrice } from '../types';
import { getGoodsSupplyCase, getGoodsSupplyData, getStocksInStores } from '../../goods-supply/selectors';
import { StoreStockData } from '../../goods-supply/types';
import { CATEGORY_SET, LAMINATE_CALCULATOR_IDS } from '../constants';

const featureRootSelector = createFeatureSelector('product');

export const getInfo = createSelector(featureRootSelector, (p) => p?.info);
export const getCharacteristics = createSelector(featureRootSelector, (p) => p?.info?.characteristics);
export const getStores = createSelector(featureRootSelector, (p) => p?.info?.stores);
export const getProductId = createSelector(getInfo, (p) => p?.productId);
export const isProductFetched = createSelector(getProductId, (productId) => !!productId);
export const isWebSellable = createSelector(getInfo, (pi) => pi?.eligibility?.webEligible === true);
export const getIsUnavailable = createSelector(
  getInfo,
  (pi) =>
    pi?.eligibility?.webEligible === false &&
    pi?.eligibility.homeDeliveryEligible === false &&
    pi?.eligibility.relayPointEligible === false &&
    pi?.eligibility.storeDeliveryEligible === false,
);

export const getEligibilityData = createSelector(getInfo, (pi) => pi?.eligibility);

export const getPrice = createSelector(getInfo, (p) => p?.price);

export const getCurrency = createSelector(getPrice, (price) => price?.currency);

export const getPrimaryPriceCb = (p: Price | undefined) => {
  if (!p) {
    return {};
  }

  return {
    price: p.main_price ? Number(p.main_price) : null,
    currency: p.currency ?? null,
    unit: p.main_uom_rus ?? null,
    prevPrice: p.previous_price ?? null,
    discountPercent: p.discount_percent ?? null,
  };
};

export const getPrimaryPrice = createSelector(getPrice, getPrimaryPriceCb);

export const getPrimaryPriceUnitSlug = createSelector(getPrice, (p) => p?.main_uom);
export const getAdditionalPriceUnitSlug = createSelector(getPrice, (p) => p?.additional_uom);
export const getPrimaryPriceUnitName = createSelector(getPrice, (p) => p?.main_uom_rus);
export const getAdditionalPriceUnitName = createSelector(getPrice, (p) => p?.additional_uom_rus);
export const getPreviousAdditionalPrice = createSelector(getPrice, (p) => p?.previous_additional_price);

export const getSecondPrice = createSelector(getPrice, (p) => ({
  ...(p?.additional_price ? { price: p?.additional_price } : {}),
  ...(p?.currency ? { currency: p?.currency } : {}),
  ...(p?.additional_uom_rus ? { unit: p?.additional_uom_rus } : {}),
}));
export const getIsPricesSwapped = createSelector(getPrice, (p) => p?.additional_as_main);
export const getDisplayedName = createSelector(getInfo, (p) => p?.displayedName);
export const getProductLink = createSelector(getInfo, (p) => p?.productLink);
export const getSource = createSelector(getInfo, (p) => p?.source);
export type ProductBelong = 'LeroyMerlin' | 'LeroyMerlinMarketplace';

export function getProductBelongFromSource(productSource: string | undefined) {
  let productBelong: 'LeroyMerlin' | 'LeroyMerlinMarketplace' = 'LeroyMerlin';
  if (productSource?.toLowerCase() === 'pim') {
    productBelong = 'LeroyMerlinMarketplace';
  }
  return productBelong;
}
export const getProductBelong = createSelector(getSource, getProductBelongFromSource);

export const getRawProductPriceCategory = createSelector(getInfo, (state) => state?.productPriceCategory);

export const getProductPriceCategoryUtil = (
  i: ProductMainInfoProps | null,
  primaryPrice: ReturnType<typeof getPrimaryPrice>,
  stocks: StoreStockData[],
) => {
  if (!i) return '';
  const isExpo =
    stocks.length !== 0 &&
    stocks.filter(({ stockValue }: { stockValue: number }) => stockValue > 0).length === 0 &&
    stocks.filter(({ displaySample }: { displaySample?: boolean }) => displaySample).length > 0;
  const prevPrice = Number.isFinite(primaryPrice?.prevPrice)
    ? Number(primaryPrice.prevPrice).toFixed(2)
    : null;
  const source = i?.source || '';
  let productTag = '';
  let category = i?.productPriceCategory as keyof typeof PriceCategoryGaNames | null;
  if (category === 'STD' && prevPrice === null) {
    category = null;
  }
  if (!category && source.toLowerCase() === 'pim') {
    productTag = 'onlineOnly';
  } else if (category && category.toLowerCase() !== 'pim' && source.toLowerCase() === 'pim') {
    productTag = `${PriceCategoryGaNames[category]},onlineOnly`;
  } else if (category) {
    productTag = PriceCategoryGaNames[category];
  }
  if (isExpo) {
    if (productTag) {
      productTag = `${productTag},expo`;
    } else {
      productTag = 'expo';
    }
  }
  return productTag;
};

export const getProductPriceCategory = createSelector(
  getInfo,
  getPrimaryPrice,
  getStocksInStores,
  getProductPriceCategoryUtil,
);

export const getCompareCategory = createSelector(getInfo, (info) => info?.compareCategory);

export const getDivisionId = createSelector(getInfo, (i) => i?.divisionId);
export const getDivisionName = createSelector(getInfo, (i) => i?.divisionName);
export const getSubDivisionId = createSelector(getInfo, (i) => i?.subDivisionId);
export const getProductModelId = createSelector(getInfo, (i) => i?.modelId);

export const getMedia = createSelector(featureRootSelector, (m) => m.media);
export const isMediaLoading = createSelector(featureRootSelector, (m) => m.mediaLoading);
export const isMediaError = createSelector(featureRootSelector, (m) => m.mediaError);
export const isMediaFetched = createSelector(featureRootSelector, (m) => !!m.media);
export const getProductImages = createSelector(getMedia, (m) => m?.images);
export const getProductVideos = createSelector(getMedia, (m) => m?.videos);
export const getManuals = createSelector(getMedia, (m) => m?.manuals);
export const getProductMarketingDescription = createSelector(
  featureRootSelector,
  (feature) => feature.info?.marketingDescription,
);

export const isMainInfoLoading = createSelector(featureRootSelector, (i) => i?.mainInfoloading);
export const isMainInfoError = createSelector(featureRootSelector, (i) => i?.mainInfoError);
export const isMainInfoFetched = createSelector(featureRootSelector, (i) => i?.info !== null);

export const getVariantsModel = createSelector(featureRootSelector, (f) => f.variants);
export const getVariantsActiveProductWidth = createSelector(getVariantsModel, (vm) => {
  const PRODUCT_WIDTH_ID = '12365';

  const model = vm?.activePoint?.axesValues.find((item) => item.id === PRODUCT_WIDTH_ID);
  return model ? Number(model.axis) : null;
});
export const isVariantsLoading = createSelector(featureRootSelector, (m) => m.variantsLoading);
export const isVariantsError = createSelector(featureRootSelector, (m) => m.variantsError);
export const isVariantsFetched = createSelector(featureRootSelector, (m) =>
  Boolean(m.variants?.variantsModel?.length && m.variants?.activePoint?.axesValues?.length),
);
export const getCategoryId = createSelector(getInfo, (info) => info?.categoryId);
export const getSubCategoryId = createSelector(getInfo, (info) => info?.subCategoryId);

export const getShouldRedirect = createSelector(
  getPathname,
  getProductLink,
  (pathName, productLink) =>
    !!productLink &&
    trimEnd(productLink, '/') !== trimEnd(pathName, '/') &&
    trimEnd(`/kz${productLink}`, '/') !== trimEnd(pathName, '/'),
);

export const isProductSet = createSelector(getInfo, (p) => {
  return p?.category === CATEGORY_SET;
});

export const getCalculatorParameters = createSelector(featureRootSelector, (m) => m.calculatorParameters);
export const isCalculatorParametersLoading = createSelector(
  featureRootSelector,
  (m) => m.calculatorParametersLoading,
);
export const isCalculatorParametersError = createSelector(
  featureRootSelector,
  (m) => m.calculatorParametersError,
);
export const isLaminateCalculator = createSelector(getCalculatorParameters, (calculatorParameters) =>
  calculatorParameters ? LAMINATE_CALCULATOR_IDS.includes(calculatorParameters.id) : false,
);

export const isCalculatorAvailable = createSelector(getInfo, (p) => p?.calculator);
export const getIsBoosterAvailable = createSelector(getInfo, (p) => p?.showBooster);

export function* selectUom() {
  const isSwapPrice = (yield select(getIsPricesSwapped)) as boolean;
  const mainUom = (yield select(getPrimaryPriceUnitName)) as string;
  const secondPrice = (yield select(getSecondPrice)) as SecondPrice;
  return isSwapPrice ? secondPrice?.unit : mainUom;
}

export function* selectProductPriceCategory() {
  return (yield select(getProductPriceCategory)) as string;
}

export const getMeasurementData = createSelector(getInfo, (p) => p?.measurementData);
export const getMerchantInfo = createSelector(featureRootSelector, (f) => f.merchant);
export const getMerchantError = createSelector(featureRootSelector, (f) => f.merchantError);

export const getGtinGtin13Master = createSelector(getInfo, (p) => p?.gtin_GTIN13_master);

export const getTintServiceData = createSelector(featureRootSelector, (product) => product.tintService);

export const getIsTintServiceAvailable = createSelector(
  getTintServiceData,
  (tintService) => tintService.isTintServiceAvailable,
);

export const getTintColors = createSelector(getTintServiceData, (tintService) => tintService.tintColors);

export const getSelectedTintColors = createSelector(
  getTintColors,
  (tintColors) => tintColors.selectedColors,
);

export const getActiveTintColor = createSelector(getTintColors, (tintColors) => tintColors.activeColor);

export const getIsTintServiceAvailableForProduct = createSelector(
  getSource,
  (source) => !!source && source.toLowerCase() === 'step',
);

export const getProductBrand = createSelector(
  getInfo,
  (info) => info?.characteristics?.find((item) => item.key === '06575')?.value,
);

export function* selectProductBrand() {
  return ((yield select(getProductBrand)) as EffectR.Select<typeof getProductBrand>) ?? null;
}

export function* selectCompareCategory() {
  return ((yield select(getCompareCategory)) as EffectR.Select<typeof getCompareCategory>) ?? null;
}

export const isOutOfStockPriceDisplayed = createSelector(
  getIsUnavailable,
  getEligibilityData,
  getGoodsSupplyCase,
  getGoodsSupplyData,
  (un, ed, gsc, gsd) => {
    const hasEligibilityData = !!ed;
    const isSupplyDataLoaded = !!gsd;
    return un || !hasEligibilityData || (isSupplyDataLoaded && (gsc === 'CASE_2' || gsc === null));
  },
);
